import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import {
  useGetOrganisationCategoriesQuery,
  useRelationshipTypeQuery,
  type GetOrganisationCategoriesQuery,
  type GetUpcomingRetentionActionsQuery,
  type RelationshipTypeQuery,
} from 'src/graphql/generated';
import { conditionOptions } from './constants';
import { ConditionType, type RetentionRuleConditions } from './types';

export type UpcomingAction =
  GetUpcomingRetentionActionsQuery['getUpcomingRetentionActions'][number];

export type RelationshipType =
  RelationshipTypeQuery['getRelationshipType'][number];

export type CaseCategory = NonNullable<
  GetOrganisationCategoriesQuery['getOrganisationCategories']
>[number];

export const DATE_FORMAT = 'D/M/YYYY';

export const getConditionLabel = (condition: string | null | undefined) =>
  conditionOptions.find((option) => option.value === condition)?.label ??
  condition;

export const useContentPadding = () => {
  const theme = useTheme<LensTheme>();
  return `0 0 0 ${theme.tokens.core.spacing[5].value}`;
};

export const useRuleConditionLookups = (
  conditions: Array<RetentionRuleConditions | null | undefined>,
) => {
  const hasRelationshipRule = conditions.some(
    (condition) =>
      condition?.condition === ConditionType.RELATIONSHIP_TO_COMPANY,
  );
  const { data: relationshipTypesData } = useRelationshipTypeQuery(undefined, {
    enabled: hasRelationshipRule,
  });

  const hasCaseCategoryRule = conditions.some(
    (condition) => condition?.condition === ConditionType.CASE_CATEGORY,
  );
  const { data: caseCategoriesData } = useGetOrganisationCategoriesQuery(
    undefined,
    { enabled: hasCaseCategoryRule },
  );

  return {
    relationshipTypes: relationshipTypesData?.getRelationshipType ?? [],
    caseCategories: caseCategoriesData?.getOrganisationCategories ?? [],
  };
};

export const getConditionValue = (
  conditions: NonNullable<UpcomingAction['RetentionRule']>['conditions'],
  relationshipTypes: RelationshipType[],
  caseCategories: CaseCategory[],
) => {
  if (conditions?.conditionValue == null) return conditions?.conditionValue;

  if (conditions.condition === ConditionType.RELATIONSHIP_TO_COMPANY) {
    return (
      relationshipTypes.find(
        (type) => type.id === Number(conditions.conditionValue),
      )?.name ?? conditions.conditionValue
    );
  }

  if (conditions.condition === ConditionType.CASE_CATEGORY) {
    const category = caseCategories.find(
      (cat) => cat.id === Number(conditions.conditionValue),
    );
    return (
      category?.locales?.['en-GB']?.name ??
      category?.locales?.['en']?.name ??
      conditions.conditionValue
    );
  }

  return conditions.conditionValue;
};

export const buildRuleFields = (
  relationshipTypes: RelationshipType[],
  caseCategories: CaseCategory[],
) =>
  [
    {
      label: 'Country',
      getValue: (action: UpcomingAction) =>
        action.RetentionRule?.country || '-',
    },
    {
      label: 'Rule type',
      getValue: (action: UpcomingAction) =>
        action.RetentionRule?.ruleType || '-',
    },
    {
      label: 'Created',
      getValue: (action: UpcomingAction) =>
        action.RetentionRule?.createdAt
          ? dayjs(action.RetentionRule.createdAt).format(DATE_FORMAT)
          : '-',
    },
    {
      label: 'Last updated',
      getValue: (action: UpcomingAction) =>
        action.RetentionRule?.updatedAt
          ? dayjs(action.RetentionRule.updatedAt).format(DATE_FORMAT)
          : '-',
    },
    {
      label: 'Conditions',
      getValue: (action: UpcomingAction) => {
        const conditions = action.RetentionRule?.conditions;

        if (!conditions) return '-';

        if (conditions.applyToAll) return 'Applies to all cases';

        if (!conditions.condition || !conditions.conditionValue) return '-';

        const conditionLabel = getConditionLabel(conditions.condition);
        const conditionValue = getConditionValue(
          conditions,
          relationshipTypes,
          caseCategories,
        );

        return (
          <>
            <strong>{conditionLabel}</strong> = {conditionValue}
          </>
        );
      },
    },
  ] as const;

const executionTimeForDay = (date: dayjs.Dayjs): dayjs.Dayjs =>
  date.utc().startOf('day').hour(1);

export const calculateTimeLeft = (dueAt: string): string => {
  const now = dayjs().utc();
  const due = dayjs(dueAt).utc();
  const target = due.isAfter(now) ? due : now;
  const candidate = executionTimeForDay(target);
  const executionTime = candidate.isAfter(now)
    ? candidate
    : candidate.add(1, 'day');
  const diffInMinutes = executionTime.diff(now, 'minute');
  const days = Math.floor(diffInMinutes / (60 * 24));
  const hours = Math.floor((diffInMinutes % (60 * 24)) / 60);
  const minutes = diffInMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m left`;
  return `${hours}h ${minutes}m left`;
};
